import React, { useState } from 'react';
import { Link } from 'react-router-dom';

const CollectionsPage: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');

  const categories = [
    { id: 'all', name: 'All' },
    { id: 'suits', name: 'Suits' },
    { id: 'jerseys', name: 'Jerseys' },
    { id: 'kits', name: 'Kits' },
    { id: 'formal', name: 'Formal' },
    { id: 'sport', name: 'Sport' },
  ];

  const collections = [
    { id: 'suits', name: 'Tailored Suits', category: 'suits', description: 'Premium suits for the boardroom, weddings, and formal occasions', image: '/images/collection/suits.jpg' },
    { id: 'jerseys', name: 'Football Jerseys', category: 'jerseys', description: 'Match-day jerseys and club wear for players and fans', image: '/images/collection/jersey.webp' },
    { id: 'kits', name: 'Training Kits', category: 'kits', description: 'Performance training wear and sportswear', image: '/images/collection/kits.jpeg' },
    { id: 'formal-wear', name: 'Formal Wear', category: 'formal', description: 'Blazers, dress shirts, and evening wear', image: '/images/collection/formal.webp' },
    { id: 'match-jerseys', name: 'Match Jerseys', category: 'jerseys', description: 'Official-style jerseys for match day and fan wear', image: '/images/collection/soccer.webp' },
    { id: 'training-gear', name: 'Training Gear', category: 'sport', description: 'Gym wear, running kits, and performance apparel', image: '/images/collection/gear.jpg' },
  ];

  const filtered = selectedCategory === 'all' ? collections : collections.filter((c) => c.category === selectedCategory);

  return (
    <div className="min-h-screen bg-[#fafafa]">
      {/* Hero */}
      <section className="pt-28 pb-14 lg:pt-36 lg:pb-20 bg-slate-900 relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="/images/collection.jpg"
            alt="Veltrix Apparel collections"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-slate-900/75" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-black/55" />
        </div>
        <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex items-center gap-2 text-sm text-white/80 mb-10 drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)]">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span className="text-white/60">/</span>
            <span className="text-white">Collections</span>
          </nav>
          <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-semibold text-white tracking-tight max-w-3xl drop-shadow-[0_2px_8px_rgba(0,0,0,0.6)]">
            Our collections
          </h1>
          <p className="mt-6 text-lg text-white max-w-2xl drop-shadow-[0_1px_4px_rgba(0,0,0,0.55)]">
            Suits, jerseys, and kits for every occasion. Manhattan's destination for men's apparel.
          </p>
          <div className="mt-12 flex flex-wrap gap-6 text-white/80 drop-shadow-[0_1px_2px_rgba(0,0,0,0.7)]">
            <span>{collections.length} collections</span>
            <span>6 categories</span>
            <span>Manhattan, NY</span>
          </div>
        </div>
      </section>

      {/* Filters */}
      <section className="sticky top-16 z-40 py-4 bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap gap-2">
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  selectedCategory === cat.id
                    ? 'bg-slate-900 text-white'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                {cat.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Grid */}
      <section className="py-14 lg:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-sm text-slate-500 mb-8">
            {filtered.length} collection{filtered.length !== 1 ? 's' : ''}
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {filtered.map((c) => (
              <Link
                key={c.id}
                to={`/collections/${c.id}`}
                className="group bg-white rounded-2xl overflow-hidden border border-slate-100 hover:border-slate-200 hover:shadow-lg transition-all duration-300"
              >
                <div className="aspect-[4/5] overflow-hidden bg-slate-100">
                  <img
                    src={c.image}
                    alt={c.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-6">
                  <span className="text-xs font-medium uppercase tracking-wider text-slate-500">{c.category}</span>
                  <h3 className="mt-2 font-display font-semibold text-slate-900 text-lg">{c.name}</h3>
                  <p className="mt-2 text-sm text-slate-600">{c.description}</p>
                  <span className="mt-4 inline-flex items-center text-sm font-medium text-slate-900 group-hover:underline">
                    View details
                    <svg className="ml-1 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" /></svg>
                  </span>
                </div>
              </Link>
            ))}
          </div>

          {filtered.length === 0 && (
            <div className="text-center py-20">
              <p className="text-slate-600">No collections in this category. Try another filter.</p>
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-white border-t border-slate-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-2xl md:text-3xl font-semibold text-slate-900 tracking-tight">
            Ready to get started?
          </h2>
          <p className="mt-3 text-slate-600 max-w-xl mx-auto">
            Contact us for custom orders, fittings, or to explore our suits, jerseys, and kits in Manhattan.
          </p>
          <Link
            to="/contact"
            className="mt-8 inline-flex items-center justify-center px-6 py-3 rounded-lg bg-slate-900 text-white font-medium text-sm hover:bg-slate-800 transition-colors"
          >
            Get in touch
          </Link>
        </div>
      </section>
    </div>
  );
};

export default CollectionsPage;
